/**
 * Lineup Player Filtering Utilities
 * 
 * Smart player filtering for dropdowns based on validation rules.
 * Filters players based on gender requirements, multi-line selection,
 * team membership, and other constraints.
 */

import { Player, LineupPosition, LineupFormat } from './lineupValidation';

export interface FilteredPlayers {
  eligible: Player[];
  ineligible: Array<{
    player: Player;
    reason: string;
  }>;
}

/**
 * Check if a player's gender is compatible with a match type
 */
function isGenderCompatibleForMatchType(
  player: Player,
  matchType: string,
  otherPlayer?: Player
): boolean {
  const playerGender = player.gender;
  const matchTypeLower = matchType.toLowerCase();

  // Unset gender is always allowed
  if (!playerGender) {
    return true;
  }

  const playerGenderLower = playerGender.toLowerCase();

  // Men's matches
  if (matchTypeLower.startsWith('mens_')) {
    return playerGenderLower === 'male';
  }

  // Women's matches
  if (matchTypeLower.startsWith('womens_')) {
    return playerGenderLower === 'female';
  }

  // Mixed doubles
  if (matchTypeLower === 'mixed_doubles') {
    if (otherPlayer?.gender) {
      // If other player has gender set, they must be different
      return playerGenderLower !== otherPlayer.gender.toLowerCase();
    }
    // If other player doesn't have gender set, allow any gender
    return true;
  }

  // Unknown match type - allow
  return true;
}

/**
 * Get players already assigned in a match lineup
 */
function getAssignedPlayersInMatch(
  matchLineup: LineupPosition[],
  excludePositionNumber?: number
): Set<number> {
  const assigned = new Set<number>();
  
  matchLineup.forEach(pos => {
    // Skip the position we're currently editing
    if (excludePositionNumber && pos.position_number === excludePositionNumber) {
      return;
    }
    
    if (pos.player1_id && pos.player1_id > 0) {
      assigned.add(pos.player1_id);
    }
    if (pos.player2_id && pos.player2_id > 0) {
      assigned.add(pos.player2_id);
    }
  });
  
  return assigned;
}

/**
 * Check if a match type is doubles
 */
function isDoublesPosition(matchType: string): boolean {
  if (!matchType) return false;
  const lowerType = matchType.toLowerCase();
  return lowerType.includes('doubles') || lowerType === 'mixed_doubles';
}

/**
 * Filter players for a dropdown based on all validation rules
 */
export function filterEligiblePlayers(
  players: Player[],
  matchType: string,
  currentMatchLineup: LineupPosition[],
  lineupFormat: LineupFormat,
  field: 'player1_id' | 'player2_id',
  currentPositionNumber: number,
  otherPlayerId?: number,
  excludePlayerId?: number, // Player to exclude (e.g., player1 from player2 dropdown)
  maxCombinedNtrpRating?: number | null // Maximum combined NTRP rating for doubles
): FilteredPlayers {
  const eligible: Player[] = [];
  const ineligible: Array<{ player: Player; reason: string }> = [];

  // Note: isSingles and isDoubles could be used for future validation
  // const isSingles = isSinglesPosition(matchType);
  // const isDoubles = isDoublesPosition(matchType);
  
  // Get the other player if provided
  const otherPlayer = otherPlayerId 
    ? players.find(p => p.id === otherPlayerId)
    : undefined;

  // Get players already assigned in this match (for multi-line selection check)
  const assignedInMatch = getAssignedPlayersInMatch(
    currentMatchLineup,
    currentPositionNumber
  );

  players.forEach(player => {
    const reasons: string[] = [];

    // Exclude specific player (e.g., player1 from player2 dropdown, or player2 from player1 dropdown)
    if (excludePlayerId && player.id === excludePlayerId) {
      reasons.push('Same player cannot be in both slots');
    }

    // Gender compatibility check
    if (!isGenderCompatibleForMatchType(player, matchType, otherPlayer)) {
      const matchTypeLower = matchType.toLowerCase();
      if (matchTypeLower.startsWith('mens_')) {
        reasons.push(`Must be male or have unset gender for ${matchType}`);
      } else if (matchTypeLower.startsWith('womens_')) {
        reasons.push(`Must be female or have unset gender for ${matchType}`);
      } else if (matchType === 'mixed_doubles') {
        if (otherPlayer?.gender) {
          reasons.push(`Must have different gender from ${otherPlayer.name} for mixed doubles`);
        }
      }
    }

    // Multi-line selection check
    if (!lineupFormat.allow_multi_line_selection) {
      if (assignedInMatch.has(player.id)) {
        // Find which positions this player is assigned to
        const assignedPositions = currentMatchLineup
          .filter(pos => 
            pos.position_number !== currentPositionNumber &&
            (pos.player1_id === player.id || pos.player2_id === player.id)
          )
          .map(pos => pos.position_number);
        
        if (assignedPositions.length > 0) {
          reasons.push(`Already assigned to position(s) ${assignedPositions.join(', ')}. Multi-line selection is not allowed.`);
        }
      }
    }

    // Max combined NTRP rating check (only for doubles positions)
    if (maxCombinedNtrpRating !== null && maxCombinedNtrpRating !== undefined && isDoublesPosition(matchType)) {
      // Get the other player's rating
      const otherPlayerRating = otherPlayer?.ntrp_rating;
      const playerRating = player.ntrp_rating;
      
      // Only filter if both players have ratings
      if (playerRating !== null && playerRating !== undefined && 
          otherPlayerRating !== null && otherPlayerRating !== undefined) {
        const combinedRating = playerRating + otherPlayerRating;
        if (combinedRating > maxCombinedNtrpRating) {
          reasons.push(`Combined NTRP rating (${combinedRating.toFixed(1)}) exceeds maximum (${maxCombinedNtrpRating.toFixed(1)})`);
        }
      }
    }

    // Team membership is assumed to be checked before calling this function
    // (only team members should be in the players array)

    if (reasons.length > 0) {
      ineligible.push({
        player,
        reason: reasons.join('; ')
      });
    } else {
      eligible.push(player);
    }
  });

  return { eligible, ineligible };
}

/**
 * Filter players for player1 dropdown
 */
export function filterPlayer1Options(
  players: Player[],
  matchType: string,
  currentMatchLineup: LineupPosition[],
  lineupFormat: LineupFormat,
  currentPositionNumber: number,
  player2Id?: number, // Accept currently selected player2Id
  maxCombinedNtrpRating?: number | null
): FilteredPlayers {
  // Use passed player2Id, or fall back to saved lineup if not provided
  const effectivePlayer2Id = player2Id !== undefined 
    ? player2Id 
    : (currentMatchLineup.find(p => p.position_number === currentPositionNumber)?.player2_id || undefined);
  
  // Only exclude if player2Id is a valid player ID (greater than 0)
  const excludePlayer2Id = effectivePlayer2Id && effectivePlayer2Id > 0 ? effectivePlayer2Id : undefined;
  
  return filterEligiblePlayers(
    players,
    matchType,
    currentMatchLineup,
    lineupFormat,
    'player1_id',
    currentPositionNumber,
    effectivePlayer2Id, // Pass player2Id for gender compatibility and NTRP rating check
    excludePlayer2Id, // Exclude player2 from player1 dropdown (only if valid player ID)
    maxCombinedNtrpRating
  );
}

/**
 * Filter players for player2 dropdown
 */
export function filterPlayer2Options(
  players: Player[],
  matchType: string,
  currentMatchLineup: LineupPosition[],
  lineupFormat: LineupFormat,
  currentPositionNumber: number,
  player1Id: number,
  maxCombinedNtrpRating?: number | null
): FilteredPlayers {
  return filterEligiblePlayers(
    players,
    matchType,
    currentMatchLineup,
    lineupFormat,
    'player2_id',
    currentPositionNumber,
    player1Id, // Other player for gender compatibility check
    player1Id, // Exclude player1 from player2 dropdown
    maxCombinedNtrpRating
  );
}

/**
 * Get all eligible players for a position considering the current state
 */
export function getEligiblePlayersForPosition(
  players: Player[],
  positionNumber: number,
  matchType: string,
  currentMatchLineup: LineupPosition[],
  lineupFormat: LineupFormat,
  field: 'player1_id' | 'player2_id',
  player1Id?: number,
  maxCombinedNtrpRating?: number | null
): Player[] {
  const result = field === 'player1_id'
    ? filterPlayer1Options(
        players,
        matchType,
        currentMatchLineup,
        lineupFormat,
        positionNumber,
        undefined, // player2Id - not available in this function
        maxCombinedNtrpRating
      )
    : filterPlayer2Options(
        players,
        matchType,
        currentMatchLineup,
        lineupFormat,
        positionNumber,
        player1Id || 0,
        maxCombinedNtrpRating
      );

  return result.eligible;
}
